import { GUN_BALLS, WEAPON_MOTION } from './ball-weapon-motion';
import { legacyFrame, LYING, STANDING, type Posture } from './character-sheet';

/**
 * Quadros da folha, inferidos da arte (o mapeamento original vivia no cliente Flash; ver `docs/assets/personagem-animacao.md`).
 * Deitado no chão é o repouso, com o piscar no fim do ciclo; ferido mostra o rosto chorando; o gesto de disparo levanta o
 * personagem, prepara o braço e arremessa.
 */
export const LYING_FRAMES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export const HURT_FRAMES = [14, 15, 16];
/**
 * Gesto de disparo, nos números de quadro do legado (`GameCharacter`: a folha tem 3 linhas de 13 quadros; `legacyFrame` converte). Um quadro por tick de 40 ms (25 por segundo).
 * Arma (`ActionType` 1): `SHOWGUN` (saca) e `SHOT` (atira). Arremesso (`ActionType` 0): `SHOWTHROWS` e `THROWS`. Depois do disparo o corpo segura o último quadro (`HIDEGUN` 27, `HIDETHROWS` 35).
 */
const GUN_SHOW = [19, 20, 21, 21, 21];
const GUN_SHOT = [22, 23, 26, 27];
const THROW_SHOW = [28, 29, 30, 30, 30];
const THROW_SHOT = [31, 32, 33, 34, 35];
export const FIRE_FRAMES = [...GUN_SHOW, ...GUN_SHOT].map(legacyFrame);
export const THROW_FRAMES = [...THROW_SHOW, ...THROW_SHOT].map(legacyFrame);
/** Ticks do disparo: o corpo segura o último quadro por `HOLD_TICKS` (o `_shootInterval` do legado) antes de guardar a arma. */
const TICK_MS = 40;
const HOLD_TICKS = 5;

/** O gesto de um disparo: quadros do corpo por tick, e o clipe da arma na mão (quadros de 1 a `frames`, rótulos `shot` e `end`; `start` é o 1). */
interface Gesture {
  show: readonly number[];
  shot: readonly number[];
  weapon: { frames: number; shot: number; end: number } | null;
}

function gestureOf(ballId: number): Gesture {
  const motion = WEAPON_MOTION[ballId];
  const weapon = motion ? { frames: motion[1], shot: motion[2], end: motion[3] } : null;
  return GUN_BALLS.has(ballId) ? { show: GUN_SHOW, shot: GUN_SHOT, weapon } : { show: THROW_SHOW, shot: THROW_SHOT, weapon };
}

/** Quando o projétil sai: quando o gesto de disparo acaba (o `ShootAction` cria a bomba depois que a ação `SHOT`/`THROWS` termina). */
export function shotReleaseMs(ballId: number): number {
  const g = gestureOf(ballId);
  return (g.show.length + g.shot.length) * TICK_MS;
}

/** Duração do gesto inteiro: disparo, espera e o fim do clipe da arma (rótulo `end` até o último quadro). */
function gestureMs(g: Gesture): number {
  const end = g.weapon ? g.weapon.frames - g.weapon.end + 1 : 0;
  return (g.show.length + g.shot.length + HOLD_TICKS + end) * TICK_MS;
}

/**
 * Quadro (1 a `frames`) do clipe da arma no tick `t` do gesto, como o legado o toca: `start` (1 até `shot`-1) ao sacar, `shot` (até `end`-1) ao atirar,
 * `end` (até o último) ao guardar. Cada trecho para no fim, e o clipe some depois do último quadro.
 */
function weaponFrameAt(g: Gesture, t: number): number | null {
  const w = g.weapon;
  if (!w) return null;
  const shotAt = g.show.length;
  const hideAt = shotAt + g.shot.length + HOLD_TICKS;
  if (t < shotAt) return Math.min(1 + t, w.shot - 1);
  if (t < hideAt) return Math.min(w.shot + (t - shotAt), w.end - 1);
  const frame = w.end + (t - hideAt);
  return frame <= w.frames ? frame : null;
}
/** Só a camada do rosto desenha o fantasma. */
export const FRAME_GHOST = 32;

const LYING_FRAME_MS = 110;
/** Andar, como `GameCharacter.WALK` do legado: os quadros 1 a 5, cada um por dois ticks de 40 ms, em ciclo enquanto o personagem anda. */
export const WALK_FRAMES = [1, 1, 2, 2, 3, 3, 4, 4, 5, 5];
const HURT_FRAME_MS = 120;

/** A arte não tem quadros de transição deitado↔em pé: o corpo cresce de `RISE_MIN` a 1 em `RISE_MS` no começo do gesto e volta no fim. */
const RISE_MS = 160;
const RISE_MIN = 0.7;
/** Depois do último deslocamento o personagem ainda conta como andando, para o ciclo não falhar entre dois passos do servidor. */
const MOVING_HOLD_MS = 120;
/** O realce vermelho some rápido; o choro dura mais, para o jogador perceber que levou o golpe. */
const HURT_FLASH_MS = 300;
const HURT_CRY_MS = 1500;

export interface PoseInput {
  alive: boolean;
  /** O personagem mudou de posição neste quadro. */
  moving: boolean;
  /** Um tiro dele está em reprodução. */
  firing: boolean;
  /** Bomba do tiro em reprodução: escolhe o gesto (sacar a arma ou arremessar) e o clipe da arma na mão. */
  ballId?: number;
}

export interface Pose {
  frame: number;
  /** Deslocamento vertical do corpo (px, negativo sobe): o balanço de quem anda. */
  bobY: number;
  /** 0 (normal) a 1 (acabou de ser ferido). */
  hurt: number;
  /** Escala vertical do corpo, de `RISE_MIN` a 1. */
  rise: number;
  posture: Posture;
  /** Quadro (de 1) do clipe da arma na mão (`shootMovie`), ou `null` quando a arma está só na folha do corpo. */
  weaponFrame: number | null;
}

/**
 * Máquina de estados de um personagem. Prioridade: morto, atirando (uma vez por tiro), ferido (choro), andando, repouso.
 * Só tempo e quadros, sem PixiJS: o desenho aplica a `Pose` que ela devolve.
 */
export class CharacterAnimator {
  private clockMs: number;
  private movingMs = 0;
  /** Tempo andando desde o último repouso: o ciclo de andar recomeça do quadro 1 depois de parar. */
  private walkMs = 0;
  private fireMs = Infinity;
  private gesture = gestureOf(0);
  private wasFiring = false;
  private flashMs = 0;
  private cryMs = 0;

  /** `phaseMs` desencontra os ciclos de jogadores diferentes. */
  constructor(phaseMs = 0) {
    this.clockMs = phaseMs;
  }

  /** O personagem levou dano: recomeça o realce e o choro. */
  hit(): void {
    this.flashMs = HURT_FLASH_MS;
    this.cryMs = HURT_CRY_MS;
  }

  update(dtMs: number, input: PoseInput): Pose {
    this.advance(dtMs, input);
    const hurt = this.flashMs / HURT_FLASH_MS;

    if (!input.alive) return { frame: FRAME_GHOST, bobY: 0, hurt: 0, rise: 1, posture: STANDING, weaponFrame: null };
    const total = gestureMs(this.gesture);
    if (this.fireMs < total) {
      const g = this.gesture;
      const tick = Math.floor(this.fireMs / TICK_MS);
      const bodyTicks = g.show.length + g.shot.length;
      const body = tick < bodyTicks ? (tick < g.show.length ? g.show[tick]! : g.shot[tick - g.show.length]!) : g.shot[g.shot.length - 1]!;
      const ramp = Math.min(1, this.fireMs / RISE_MS, (total - this.fireMs) / RISE_MS);
      return { frame: legacyFrame(body), bobY: 0, hurt, rise: RISE_MIN + (1 - RISE_MIN) * ramp, posture: STANDING, weaponFrame: weaponFrameAt(g, tick) };
    }

    const walking = this.movingMs > 0;
    // Sem balanço vertical: o legado não move o corpo ao andar, só troca os quadros (o chão já ergue e abaixa o personagem a cada passo).
    const lying = (frame: number): Pose => ({ frame, bobY: 0, hurt, rise: 1, posture: LYING, weaponFrame: null });
    if (this.cryMs > 0) return lying(cycle(HURT_FRAMES, HURT_CRY_MS - this.cryMs, HURT_FRAME_MS));
    if (walking) return lying(cycle(WALK_FRAMES, Math.max(0, this.walkMs - dtMs), TICK_MS));
    return lying(cycle(LYING_FRAMES, this.clockMs, LYING_FRAME_MS));
  }

  private advance(dtMs: number, input: PoseInput): void {
    this.clockMs += dtMs;
    this.flashMs = Math.max(0, this.flashMs - dtMs);
    this.cryMs = Math.max(0, this.cryMs - dtMs);
    this.movingMs = input.moving ? MOVING_HOLD_MS : Math.max(0, this.movingMs - dtMs);
    this.walkMs = this.movingMs > 0 ? this.walkMs + dtMs : 0;
    // Um tiro novo (a subida de `firing`) recomeça o gesto; enquanto o mesmo tiro segue no ar, ele não repete.
    if (input.firing && !this.wasFiring) {
      this.fireMs = 0;
      this.gesture = gestureOf(input.ballId ?? 0);
    } else this.fireMs += dtMs;
    this.wasFiring = input.firing;
  }
}

/** Quadro que a lista mostra depois de `elapsedMs`, repetindo-se a cada `frameMs` por quadro. */
function cycle(frames: readonly number[], elapsedMs: number, frameMs: number): number {
  return frames[Math.floor(elapsedMs / frameMs) % frames.length]!;
}
